<template>
  <div>
    <div class="headline5 mb-3">
      {{ $t("modals.createWorkspace.workspaceSettings") }}
    </div>
    <v-card>
      <v-card-text>
        <cui-locale-select
          v-model="workspace.languageCode"
          :locale-labels="localeDisplayNames"
          :labels="localeSelectLabels"
          test-id-prefix="create-workspace-modal"
        />

        <v-autocomplete
          v-if="showLabelsField"
          v-model="workspace.labels"
          class="mt-4"
          :placeholder="$t('modals.editWorkspace.selectLabel')"
          :label="$t('modals.editWorkspace.selectLabel')"
          :hide-no-data="false"
          :no-filter="true"
          :menu-props="{
            maxHeight: '250',
          }"
          :items="labelOptions"
          variant="outlined"
          multiple
          return-object
          item-value="id"
          item-title="name"
          @update:search="$emit('label-search', $event)"
        >
          <template v-slot:chip="{ props, item }">
            <v-chip
              v-bind="props"
              :closable="true"
              variant="flat"
              size="default"
              :color="item.raw.color"
              close-icon="$closeCircle"
              :text="item.title"
              label
              rounded="3"
            >
              <span class="coro-label text-uppercase text-primary">
                {{ item.title }}
              </span>
            </v-chip>
          </template>
          <template #item="{ item, props }">
            <v-list-item v-bind="props" title="">
              <v-list-item-title title="">
                <div class="d-flex align-center label-popup-block">
                  <div class="color-preview mr-2" :style="{ 'background-color': item.raw.color }" />
                  <span>
                    {{ item.raw.name }}
                  </span>
                </div>
              </v-list-item-title>
            </v-list-item>
          </template>
        </v-autocomplete>

        <template v-if="showGlobalPolicy">
          <div class="subtitle1 mb-4">
            {{ $t("modals.createWorkspace.globalProtectionPoliciesTitle") }}
          </div>
          <v-select
            v-model="workspace.mspPolicyId"
            :items="globalPolicies"
            data-testid="global-policy-select"
            class="mb-4"
            item-value="id"
            item-title="name"
            :rules="[required()]"
            :label="$t('modals.createWorkspace.mspPolicyIdLabel')"
            :placeholder="$t('modals.createWorkspace.mspPolicyIdLabel')"
            variant="outlined"
            hide-details="auto"
            auto-select-first="exact"
            :loading="globalPoliciesLoading"
          >
            <template #append-item>
              <div v-intersect="onGlobalPolicyIntersect" />
            </template>
          </v-select>

          <v-switch
            v-model="workspace.allowPolicyOverriding"
            :ripple="false"
            density="compact"
            hide-details
            color="primary"
            inset
            flat
            data-testid="allow-local-overriding-switch"
          >
            <template #label>
              <div class="body2">
                {{ $t("modals.createWorkspace.allowLocalOverriding") }}
              </div>
            </template>
          </v-switch>
        </template>

        <cui-billing-fields
          v-if="showBillingFields"
          :model-value="workspace"
          :workspace-type="workspace.type"
          :parent-workspace-id="workspace.parentWorkspaceId"
          :labels="billingLabels"
          test-id-prefix="create-workspace"
          @update:model-value="$emit('update:workspace', $event)"
        />

        <cui-notification-level-select
          v-model="workspace.notificationsLevel"
          :workspace-type="workspace.type"
          :has-parent="hasParentWorkspace"
          :labels="notificationLabels"
          test-id-prefix="add-workspace-modal"
        />
        <cui-workspace-advanced-settings
          :model-value="workspace"
          :switches="advancedSettingSwitches"
          :labels="advancedSettingsLabels"
          test-id-prefix="create-workspace-modal"
          @update:model-value="$emit('update:workspace', $event)"
        />
      </v-card-text>
    </v-card>
  </div>
</template>

<script lang="ts">
import { defineComponent, type PropType } from "vue";
import {
  CuiBillingFields,
  CuiLocaleSelect,
  CuiNotificationLevelSelect,
  type CuiNotificationLevelSelectLabels,
  CuiWorkspaceAdvancedSettings,
  type WorkspaceAdvancedSetting,
} from "@coronet/cui";
import type { MspWorkspaceDialogItem } from "@/_store/msp/msp.module.ts";
import type { MspLabel } from "@/_store/msp/msp-labels.module.ts";
import type { GlobalPolicyListItem } from "@/_store/msp/global-policies/msp-global-policies.module.ts";
import { required } from "@/_helpers/validators";
import { useWorkspaceFormText } from "@/composables/useWorkspaceFormText";

/**
 * Workspace settings step of the MSP create-workspace wizard. The dialog owns the item, the label
 * and global-policy lookups and the policy flags; the step edits the item in place and passes
 * whole-item updates of the billing fields and advanced settings out as `update:workspace`.
 */
export default defineComponent({
  components: {
    CuiBillingFields,
    CuiLocaleSelect,
    CuiNotificationLevelSelect,
    CuiWorkspaceAdvancedSettings,
  },
  props: {
    workspace: {
      type: Object as PropType<MspWorkspaceDialogItem>,
      required: true,
    },
    showLabelsField: {
      type: Boolean,
      required: true,
    },
    labelOptions: {
      type: Array as PropType<MspLabel[]>,
      required: true,
    },
    showGlobalPolicy: {
      type: Boolean,
      required: true,
    },
    globalPolicies: {
      type: Array as PropType<GlobalPolicyListItem[]>,
      required: true,
    },
    globalPoliciesLoading: {
      type: Boolean,
      required: true,
    },
    showBillingFields: {
      type: Boolean,
      required: true,
    },
    hasParentWorkspace: {
      type: Boolean,
      required: true,
    },
    notificationLabels: {
      type: Object as PropType<CuiNotificationLevelSelectLabels>,
      required: true,
    },
    advancedSettingSwitches: {
      type: Array as PropType<WorkspaceAdvancedSetting[]>,
      required: true,
    },
  },
  emits: ["update:workspace", "label-search", "global-policy-intersect"],
  setup(_props, { emit }) {
    const { billingLabels, localeSelectLabels, localeDisplayNames, advancedSettingsLabels } =
      useWorkspaceFormText();

    const onGlobalPolicyIntersect = (
      isIntersecting: boolean,
      entries: IntersectionObserverEntry[]
    ) => emit("global-policy-intersect", isIntersecting, entries);

    return {
      required,
      billingLabels,
      localeSelectLabels,
      localeDisplayNames,
      advancedSettingsLabels,
      onGlobalPolicyIntersect,
    };
  },
});
</script>

<style lang="scss" scoped>
// The dialog's `.stepper-step__wrapper .v-card` rule reaches only cards in its own template.
.v-card {
  border-radius: 8px !important;
}

.color-preview {
  width: 20px;
  height: 20px;
  border-radius: 4px;
}
</style>
